import React, { useEffect } from 'react'

import { useLocation, useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { message } from 'antd'
import { useTheme } from 'styled-components'

import { login } from 'src/Router/routes'
import { FullWideNotification } from 'src/Components/FullWideNotification'
import { setLoginTypeToInitialState } from 'src/Redux/auth-process/authTypeSlice'
import { useAppDispatch } from 'src/Hooks/redux'
import { getAuthType } from 'src/Redux/auth-process/authTypeSlice/selectors'
import { locationEvent, pageViewEvent } from 'src/Helpers/TagManager'
import { pageInfo } from 'src/Helpers/TagManager/pageInfo'
import { clearPendingSpotifyCuratorAuth } from 'src/Utils/pendingSpotifyCuratorAuth'

const DELAY = 3000

const SPOTIFY_ERROR_MESSAGES: Record<string, string> = {
  spotify_auth_cancelled: 'Spotify login was cancelled. Please try again.',
  spotify_email_missing:
    'Your Spotify account has no email address. Please add one to continue.',
}

const getSpotifyErrorMessage = (errorCode: string): string =>
  SPOTIFY_ERROR_MESSAGES[errorCode] ??
  'Spotify login failed. Please try again.'

export const useLogin = (): {
  contextHolder: JSX.Element
} => {
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useAppDispatch()
  const theme = useTheme()
  const authType = useSelector(getAuthType)
  const [messageApi, contextHolder] = message.useMessage()

  useEffect(() => {
    locationEvent()
    pageViewEvent({ currentPageInfo: pageInfo.Login })
  }, [])

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    if (location.pathname !== login) {
      navigate({
        pathname: 'login',
        search: searchParams.toString(),
      })
    }
  }, [navigate, location])

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)

    if (location.search.includes('blacklist')) {
      navigate(login)
      void messageApi.open({
        content: (
          <FullWideNotification message='Sorry, our platform is no longer available for users from your country' />
        ),
        duration: 3,
        style: { backgroundColor: theme.color.mediumRed },
      })
    } else {
      const spotifyError = searchParams.get('error')
      if (spotifyError && !searchParams.has('succeeded')) {
        clearPendingSpotifyCuratorAuth()
        navigate(login, { replace: true })
        void messageApi.open({
          content: (
            <FullWideNotification
              message={getSpotifyErrorMessage(spotifyError)}
            />
          ),
          duration: 3,
          style: { backgroundColor: theme.color.mediumRed },
        })
      }
    }

    return () => {
      dispatch(setLoginTypeToInitialState())
    }
  }, [location, messageApi, navigate, dispatch, theme.color.mediumRed])

  useEffect(() => {
    let id: ReturnType<typeof setTimeout>
    if (authType === 'userIsNotExist') {
      id = setTimeout(() => {
        dispatch(setLoginTypeToInitialState())
      }, DELAY)
    }
    return () => {
      clearTimeout(id)
    }
  }, [authType, dispatch])

  return { contextHolder }
}
